import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeft, Library as LibraryIcon, Search, Download, FileText,
  BookOpen, ScrollText, Wrench, ShieldCheck, ClipboardList, Clock, Star, Sparkles,
} from 'lucide-react';
import BottomNav from '../components/BottomNav';
import DesktopSidebar from '../components/DesktopSidebar';

const API = process.env.REACT_APP_BACKEND_URL;

const CAT_META = {
  contract:   { label: 'CBA',         Icon: BookOpen,     color: '#10b981', bg: 'rgba(16,185,129,0.10)',  border: 'rgba(16,185,129,0.4)',
                sub: 'Collective Bargaining Agreement + core contract' },
  memorandum: { label: 'MOA / Letter', Icon: ScrollText,   color: '#a78bfa', bg: 'rgba(167,139,250,0.10)', border: 'rgba(167,139,250,0.4)',
                sub: 'Memoranda of agreement, side letters, awards' },
  procedure:  { label: 'Procedure',   Icon: Wrench,       color: '#60a5fa', bg: 'rgba(96,165,250,0.10)',  border: 'rgba(96,165,250,0.4)',
                sub: 'Work practices + operational how-tos' },
  policy:     { label: 'Policy',      Icon: ClipboardList,color: '#fbbf24', bg: 'rgba(251,191,36,0.10)',  border: 'rgba(251,191,36,0.4)',
                sub: 'Corporate + company-wide policies' },
  safety:     { label: 'Safety',      Icon: ShieldCheck,  color: '#fb923c', bg: 'rgba(251,146,60,0.10)',  border: 'rgba(251,146,60,0.4)',
                sub: 'PPE, LOTO, incident response, hazards' },
};

const STATS_KEY = 'askstewie_book_stats_v1';

function loadStats() {
  try {
    const raw = localStorage.getItem(STATS_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch { return {}; }
}
function recordOpen(bookId) {
  const stats = loadStats();
  const cur = stats[bookId] || { count: 0, lastOpenedAt: 0 };
  stats[bookId] = { count: cur.count + 1, lastOpenedAt: Date.now() };
  try { localStorage.setItem(STATS_KEY, JSON.stringify(stats)); } catch { /* quota */ }
  return stats;
}

export default function LibraryPage() {
  const navigate = useNavigate();
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [stats, setStats] = useState(loadStats());

  useEffect(() => {
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const r = await axios.get(`${API}/api/books/list`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        setBooks(Array.isArray(r.data) ? r.data : []);
      } catch (e) {
        setError('Could not load the library. Try again later.');
      }
      setLoading(false);
    })();
  }, []);

  const downloadBook = useCallback(async (book) => {
    setStats(recordOpen(book.id));
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/api/book/download/${book.id}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      console.warn('Book download failed:', e);
    }
  }, []);

  const filtered = books.filter((b) => {
    if (categoryFilter !== 'all' && b.category !== categoryFilter) return false;
    if (query.trim()) {
      const q = query.toLowerCase();
      if (!(b.title || '').toLowerCase().includes(q)
          && !(b.filename || '').toLowerCase().includes(q)) return false;
    }
    return true;
  });

  const counts = books.reduce((acc, b) => {
    acc[b.category] = (acc[b.category] || 0) + 1;
    return acc;
  }, {});

  const recent = useMemo(() => {
    return books
      .filter((b) => stats[b.id]?.lastOpenedAt)
      .sort((a, b) => (stats[b.id]?.lastOpenedAt || 0) - (stats[a.id]?.lastOpenedAt || 0))
      .slice(0, 5);
  }, [books, stats]);

  const frequent = useMemo(() => {
    return books
      .filter((b) => (stats[b.id]?.count || 0) >= 2)
      .sort((a, b) => (stats[b.id]?.count || 0) - (stats[a.id]?.count || 0))
      .slice(0, 5);
  }, [books, stats]);

  // "Popular" fallback = biggest documents by chunk_count (usually the CBA + big MOAs)
  const popular = useMemo(() => {
    return [...books]
      .sort((a, b) => (b.chunk_count || 0) - (a.chunk_count || 0))
      .slice(0, 5);
  }, [books]);

  const showStrips = !query.trim() && categoryFilter === 'all';

  return (
    <div className="lib-root" data-testid="library-page">
      <style>{styles}</style>
      <DesktopSidebar />

      <header className="lib-header">
        <button
          type="button"
          className="lib-back"
          onClick={() => navigate('/')}
          aria-label="Back"
          data-testid="library-back"
        >
          <ArrowLeft size={18} />
        </button>
        <div className="lib-header-text">
          <LibraryIcon size={18} className="lib-header-icon" />
          <span>Library</span>
        </div>
        <span className="lib-header-count">{books.length}</span>
      </header>

      <div className="lib-search">
        <Search size={16} className="lib-search-icon" />
        <input
          type="text"
          placeholder="Search agreements, MOAs, awards…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="lib-search-input"
          data-testid="library-search-input"
        />
      </div>

      <div className="lib-filters" data-testid="library-filters">
        {[
          { key: 'all',        label: `All · ${books.length}` },
          { key: 'contract',   label: `CBA · ${counts.contract || 0}` },
          { key: 'memorandum', label: `MOAs · ${counts.memorandum || 0}` },
          { key: 'procedure',  label: `Procedure · ${counts.procedure || 0}` },
          { key: 'safety',     label: `Safety · ${counts.safety || 0}` },
          { key: 'policy',     label: `Policy · ${counts.policy || 0}` },
        ].filter((f) => f.key === 'all' || (counts[f.key] || 0) > 0).map((f) => (
          <button
            key={f.key}
            type="button"
            className={`lib-filter ${categoryFilter === f.key ? 'lib-filter--active' : ''}`}
            onClick={() => setCategoryFilter(f.key)}
            data-testid={`library-filter-${f.key}`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {!loading && !error && showStrips && (
        <section className="lib-cats" data-testid="library-category-cards">
          {Object.entries(CAT_META).map(([key, meta]) => {
            const n = counts[key] || 0;
            if (n === 0) return null;
            const CIcon = meta.Icon;
            return (
              <button
                key={key}
                type="button"
                className="lib-cat"
                style={{ color: meta.color, background: meta.bg, borderColor: meta.border }}
                onClick={() => setCategoryFilter(key)}
                data-testid={`library-category-card-${key}`}
              >
                <span className="lib-cat-icon" style={{ background: meta.bg, borderColor: meta.border }}>
                  <CIcon size={18} />
                </span>
                <span className="lib-cat-main">
                  <span className="lib-cat-title">{meta.label}</span>
                  <span className="lib-cat-sub">{meta.sub}</span>
                </span>
                <span className="lib-cat-count">{n}</span>
              </button>
            );
          })}
        </section>
      )}

      {!loading && !error && showStrips && recent.length > 0 && (
        <StripRow
          testid="library-strip-recent"
          title="Recently Viewed"
          Icon={Clock}
          tint="#67e8f9"
          books={recent}
          stats={stats}
          onOpen={downloadBook}
        />
      )}

      {!loading && !error && showStrips && frequent.length > 0 && (
        <StripRow
          testid="library-strip-frequent"
          title="Frequently Used"
          Icon={Star}
          tint="#fbbf24"
          books={frequent}
          stats={stats}
          onOpen={downloadBook}
        />
      )}

      {!loading && !error && showStrips && recent.length === 0 && popular.length > 0 && (
        <StripRow
          testid="library-strip-popular"
          title="Most Referenced"
          Icon={Sparkles}
          tint="#a78bfa"
          books={popular}
          stats={stats}
          onOpen={downloadBook}
          subtitle="Open one to seed your Recently Viewed & Frequently Used lists"
        />
      )}

      <main className="lib-main">
        {loading && <div className="lib-empty">Loading…</div>}
        {!loading && error && <div className="lib-empty">{error}</div>}
        {!loading && !error && filtered.length === 0 && (
          <div className="lib-empty">No documents match your search.</div>
        )}

        <ul className="lib-list" data-testid="library-list">
          {filtered.map((b) => {
            const cat = CAT_META[b.category] || { label: 'Doc', color: '#a1a1aa', bg: 'rgba(255,255,255,0.04)', border: 'rgba(255,255,255,0.12)' };
            return (
              <li key={b.id} className="lib-item" data-testid={`library-item-${b.id}`}>
                <FileText size={18} className="lib-item-icon" />
                <div className="lib-item-main">
                  <div className="lib-item-title">{b.title}</div>
                  <div className="lib-item-meta">
                    <span
                      className="lib-item-chip"
                      style={{ color: cat.color, background: cat.bg, borderColor: cat.border }}
                    >{cat.label}</span>
                    {b.chunk_count ? <span>{b.chunk_count} chunks</span> : null}
                  </div>
                </div>
                <button
                  type="button"
                  className="lib-item-dl"
                  onClick={() => downloadBook(b)}
                  data-testid={`library-download-${b.id}`}
                  aria-label="Download original"
                >
                  <Download size={14} />
                </button>
              </li>
            );
          })}
        </ul>

        <div className="lib-bottom-spacer" />
      </main>

      <BottomNav />
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────── */

function StripRow({ testid, title, Icon, tint, subtitle, books, stats, onOpen }) {
  return (
    <section className="lib-strip" data-testid={testid}>
      <header className="lib-strip-head" style={{ color: tint }}>
        <Icon size={14} />
        <h3 className="lib-strip-title">{title}</h3>
        {subtitle && <span className="lib-strip-sub">{subtitle}</span>}
      </header>
      <div className="lib-strip-row">
        {books.map((b) => {
          const cat = CAT_META[b.category] || { color: '#a1a1aa', bg: 'rgba(255,255,255,0.04)', border: 'rgba(255,255,255,0.12)', Icon: FileText, label: 'Doc' };
          const CIcon = cat.Icon;
          const c = stats[b.id]?.count || 0;
          return (
            <button
              key={b.id}
              type="button"
              className="lib-strip-card"
              onClick={() => onOpen(b)}
              data-testid={`library-strip-card-${b.id}`}
              style={{ borderColor: cat.border }}
            >
              <span className="lib-strip-card-icon" style={{ color: cat.color, background: cat.bg }}>
                <CIcon size={16} />
              </span>
              <span className="lib-strip-card-title">{b.title}</span>
              <span className="lib-strip-card-meta">
                <span className="lib-strip-card-chip" style={{ color: cat.color, borderColor: cat.border, background: cat.bg }}>
                  {cat.label}
                </span>
                {c > 0 && <span className="lib-strip-card-count">Opened {c}×</span>}
              </span>
            </button>
          );
        })}
      </div>
    </section>
  );
}

const styles = `
.lib-root {
  min-height: 100vh;
  background: linear-gradient(180deg, #050507 0%, #0a0a0e 100%);
  color: #e4e4e7;
  font-family: -apple-system, BlinkMacSystemFont, "Inter", system-ui, sans-serif;
  padding-bottom: 80px;
}
.lib-header {
  position: sticky; top: 0; z-index: 10;
  display: flex; align-items: center; gap: 10px;
  padding: 14px 18px;
  background: rgba(10,10,14,0.92);
  backdrop-filter: blur(12px);
  border-bottom: 1px solid rgba(255,255,255,0.05);
  max-width: 540px; margin: 0 auto;
}
@media (min-width: 720px) { .lib-header { max-width: 720px; } }
.lib-back {
  width: 36px; height: 36px; border-radius: 10px;
  background: rgba(255,255,255,0.05);
  border: 1px solid rgba(255,255,255,0.08);
  color: #d4d4d8; cursor: pointer;
  display: grid; place-items: center;
}
.lib-back:hover { background: rgba(255,255,255,0.08); }
.lib-header-text {
  display: flex; align-items: center; gap: 8px;
  font-size: 17px; font-weight: 700; color: #f4f4f5;
  flex: 1;
}
.lib-header-icon { color: #22d3ee; }
.lib-header-count {
  padding: 3px 9px; border-radius: 999px;
  background: rgba(34,211,238,0.1);
  border: 1px solid rgba(34,211,238,0.3);
  color: #67e8f9; font-size: 11px; font-weight: 700;
}

.lib-search {
  max-width: 540px; margin: 14px auto 0; padding: 0 18px;
  display: flex; align-items: center; gap: 8px;
}
@media (min-width: 720px) { .lib-search { max-width: 720px; } }
.lib-search-icon { color: #71717a; }
.lib-search-input {
  flex: 1;
  background: rgba(24,24,27,0.95);
  border: 1px solid rgba(63,63,70,0.65);
  border-radius: 12px;
  padding: 10px 12px;
  color: #f4f4f5; font-size: 14px; outline: none;
  transition: border-color 0.15s;
}
.lib-search-input:focus { border-color: rgba(34,211,238,0.55); }

.lib-filters {
  max-width: 540px; margin: 12px auto 0; padding: 0 18px;
  display: flex; gap: 6px; overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
}
@media (min-width: 720px) { .lib-filters { max-width: 720px; } }
.lib-filters::-webkit-scrollbar { display: none; }
.lib-filter {
  flex-shrink: 0;
  padding: 6px 12px;
  background: rgba(24,24,27,0.7);
  border: 1px solid rgba(63,63,70,0.6);
  border-radius: 999px;
  color: #a1a1aa;
  font-size: 12px; font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;
}
.lib-filter:hover { color: #e4e4e7; border-color: rgba(34,211,238,0.4); }
.lib-filter--active {
  background: rgba(34,211,238,0.12);
  border-color: rgba(34,211,238,0.55);
  color: #67e8f9;
}

/* Colorful category cards */
.lib-cats {
  max-width: 540px; margin: 16px auto 0; padding: 0 18px;
  display: grid;
  grid-template-columns: 1fr;
  gap: 8px;
}
@media (min-width: 720px) { .lib-cats { max-width: 720px; grid-template-columns: 1fr 1fr; } }
.lib-cat {
  display: flex; align-items: center; gap: 12px;
  padding: 12px 14px;
  border: 1px solid; border-radius: 14px;
  cursor: pointer;
  text-align: left;
  font-family: inherit;
  transition: transform 160ms ease-out, filter 160ms;
}
.lib-cat:hover { transform: translateY(-1px); filter: brightness(1.1); }
.lib-cat-icon {
  width: 34px; height: 34px; border-radius: 10px;
  display: grid; place-items: center;
  border: 1px solid; color: inherit; flex-shrink: 0;
}
.lib-cat-main { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 2px; }
.lib-cat-title { font-weight: 800; font-size: 14px; color: #f4f4f5; }
.lib-cat-sub   { font-weight: 500; font-size: 11.5px; color: #a1a1aa; line-height: 1.35; }
.lib-cat-count {
  padding: 4px 10px; border-radius: 999px;
  border: 1px solid currentColor;
  background: rgba(255,255,255,0.04);
  font-size: 12px; font-weight: 800;
  flex-shrink: 0;
}

/* Recently Viewed / Frequently Used strips */
.lib-strip {
  max-width: 540px; margin: 16px auto 0; padding: 0 18px;
}
@media (min-width: 720px) { .lib-strip { max-width: 720px; } }
.lib-strip-head {
  display: flex; align-items: center; gap: 6px;
  margin-bottom: 8px;
  font-weight: 800; letter-spacing: 0.02em;
}
.lib-strip-title { margin: 0; font-size: 12px; text-transform: uppercase; }
.lib-strip-sub   {
  margin-left: 6px;
  font-size: 11px; font-weight: 500; text-transform: none; letter-spacing: 0;
  color: #71717a;
}
.lib-strip-row {
  display: flex; gap: 10px; overflow-x: auto;
  padding-bottom: 4px;
  scrollbar-width: none;
}
.lib-strip-row::-webkit-scrollbar { display: none; }
.lib-strip-card {
  flex-shrink: 0;
  width: 200px;
  display: flex; flex-direction: column; gap: 8px;
  padding: 12px;
  background: rgba(24,24,27,0.6);
  border: 1px solid;
  border-radius: 12px;
  text-align: left; cursor: pointer;
  font-family: inherit;
  color: #f4f4f5;
  transition: transform 160ms, background 160ms;
}
.lib-strip-card:hover {
  transform: translateY(-2px);
  background: rgba(24,24,27,0.85);
}
.lib-strip-card-icon {
  width: 32px; height: 32px; border-radius: 8px;
  display: grid; place-items: center;
  border: 1px solid transparent;
}
.lib-strip-card-title {
  font-size: 13px; font-weight: 700;
  line-height: 1.3;
  display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;
}
.lib-strip-card-meta {
  display: flex; align-items: center; gap: 6px; flex-wrap: wrap;
  font-size: 11px; color: #a1a1aa;
}
.lib-strip-card-chip {
  padding: 1px 8px; border: 1px solid; border-radius: 5px;
  font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.04em;
}
.lib-strip-card-count { font-weight: 600; color: #71717a; }


.lib-main { max-width: 540px; margin: 0 auto; padding: 14px 18px 16px; }
@media (min-width: 720px) { .lib-main { max-width: 720px; } }
.lib-empty {
  padding: 40px 16px;
  text-align: center;
  color: #71717a; font-size: 13px;
}
.lib-list { list-style: none; padding: 0; margin: 0; display: flex; flex-direction: column; gap: 8px; }
.lib-item {
  display: flex; align-items: center; gap: 12px;
  padding: 12px 14px;
  background: rgba(24,24,27,0.6);
  border: 1px solid rgba(63,63,70,0.4);
  border-radius: 12px;
}
.lib-item-icon { color: #71717a; flex-shrink: 0; }
.lib-item-main { flex: 1; min-width: 0; }
.lib-item-title {
  font-size: 13px; font-weight: 600; color: #f4f4f5;
  line-height: 1.3;
  word-break: break-word;
}
.lib-item-meta {
  margin-top: 4px;
  display: flex; align-items: center; gap: 8px;
  font-size: 11px; color: #71717a;
}
.lib-item-chip {
  padding: 1px 8px; border: 1px solid; border-radius: 5px;
  font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.04em;
}
.lib-item-dl {
  flex-shrink: 0;
  width: 34px; height: 34px;
  border-radius: 10px;
  background: rgba(34,211,238,0.08);
  border: 1px solid rgba(34,211,238,0.3);
  color: #67e8f9; cursor: pointer;
  display: grid; place-items: center;
  transition: all 0.15s;
}
.lib-item-dl:hover { background: rgba(34,211,238,0.18); border-color: rgba(34,211,238,0.5); }
.lib-bottom-spacer { height: 16px; }
`;
